import React from 'react';
import { Brain, Zap, Target, TrendingUp } from 'lucide-react';
import Layout from './Layout';
import FadeIn from './FadeIn';

const AISection: React.FC = () => {
  const features = [
    {
      icon: <Brain className="w-12 h-12" />,
      title: "IA Contextuelle",
      description: "Notre IA analyse votre entreprise et votre secteur pour générer des offres parfaitement adaptées à votre contexte."
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Optimisation Continue",
      description: "L'IA apprend de chaque interaction pour améliorer constamment la qualité des offres générées."
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: "Personnalisation Avancée",
      description: "Adaptation automatique du ton et du style selon votre culture d'entreprise et vos valeurs."
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Analyse Prédictive",
      description: "Prédiction de l'efficacité de vos offres basée sur des millions de données de recrutement."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <Layout>
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-h2-mobile md:text-h2 mb-4">
              La puissance de l'<span className="gradient-text">Intelligence Artificielle</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Notre technologie d'IA de pointe révolutionne la façon dont vous créez et optimisez vos offres d'emploi.
            </p>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <FadeIn key={index} delay={index * 100}>
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-indigo-600 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-h3-mobile md:text-h3 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={200}>
          <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-h3-mobile md:text-h3 mb-4">ROI Démontré</h3>
                <ul className="space-y-8">
                  <li className="flex items-start">
                    <span className="w-12 text-2xl mr-4">⚡️</span>
                    <div>
                      <p className="text-gray-700 font-medium">Réduction du temps de rédaction (jusqu'à 80%)</p>
                      <p className="text-sm text-gray-600 mt-1">"Utiliser une IA pour la rédaction d'une offre d'emploi permet de réduire le temps de création de 12 à 4 minutes."</p>
                      <a href="https://www.flatchr.io/blog/ia-recrutement-accelerer-optimiser-processus-rh" target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 inline-block">🔗 Flatchr – IA et recrutement</a>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-12 text-2xl mr-4">📈</span>
                    <div>
                      <p className="text-gray-700 font-medium">Amélioration du taux de réponse via SEO</p>
                      <p className="text-sm text-gray-600 mt-1">"Une offre bien structurée, avec les bons mots-clés, améliore sa visibilité sur les moteurs de recherche et donc son taux de réponse."</p>
                      <a href="https://www.inzejob.com/article/42753/comment-lia-peut-elle-aider-les-recruteurs-a-rediger-les-offres-demploi" target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 inline-block">🔗 Inzejob – Comment l'IA aide à rédiger les offres</a>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-12 text-2xl mr-4">🎯</span>
                    <div>
                      <p className="text-gray-700 font-medium">Augmentation des candidatures pertinentes</p>
                      <p className="text-sm text-gray-600 mt-1">"Grâce au matching algorithmique, l'IA recommande les profils les plus pertinents, améliorant ainsi la qualité des candidatures."</p>
                      <a href="https://recruteur.lefigaro.fr/article/ia-et-recrutement/" target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 inline-block">🔗 Le Figaro Recruteur – IA et recrutement</a>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-12 text-2xl mr-4">💰</span>
                    <div>
                      <p className="text-gray-700 font-medium">ROI Inbound Recruiting / IA</p>
                      <p className="text-sm text-gray-600 mt-1">"L'inbound recruiting permet une réduction de 85 % du coût d'acquisition des candidats après 1 an."</p>
                      <a href="https://inbound.lasuperagence.com/en/blog-agency/roi-statistiques-stats-inbound-recruiting" target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 inline-block">🔗 La Super Agence – Statistiques ROI Inbound Recruiting</a>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Analytics Dashboard"
                  className="rounded-lg shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Layout>
    </section>
  );
};

export default AISection;